import { Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { Subscription, forkJoin, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { CreditsPartIIIService } from './credits-part-iii.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
import { safeEvalFormula } from '../../../shared/_utils/safe-eval-formula';

@Component({
    selector: 'gtw-credits-part-iii',
    templateUrl: './credits-part-iii.component.html',
    styleUrls: ['./credits-part-iii.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CreditsPartIIIComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  /**
   * Diagnostic flag for verifying calculated-field behavior. When set to true,
   * every formula evaluation (arithmetic or JSON-conditional) prints a line to
   * the browser console showing the source formula, the substituted formula,
   * the target line/attribute, the old value and the new computed value.
   *
   * Switch to true, reload the modal, edit a field and watch the console to
   * see every triggered calculation. Switch back to false before commit.
   */
  static readonly DEBUG_CALC = false;

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();
  private isInitialized = false;

  baseURLs: any = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};
  gridData: any;

  modalTitle = 'Edit Credit Forms';

  get formNo(): string {
    return (this.activeTab || '').split('~')[0];
  }

  get showF7218PartIIISection(): boolean {
    return this.formNo === 'F7218' && this.showF7218PartIIITable;
  }

  /**
   * Activates the F8936 wizard (Form 8936 + Schedule A tabs) when the form
   * being edited is Part II or Part V of F8936 in tax year 2024 or later.
   * Compare on the first SUB_FORM segment (formName) rather than the raw
   * subForm string, because rowData.SUB_FORM can arrive as `F8936PII`,
   * `F8936PII~~~`, or `F8936PII~A~II~Section`; the legacy AngularJS code
   * joined those segments with `-` and compared to `'F8936PII'`, which only
   * matched when all extra segments were empty. Comparing on `formName`
   * matches regardless of whether/how SUB_FORM is segmented.
   */
  get isF8936Wizard(): boolean {
    const formName = this.formDataObj?.formName || '';
    return (formName === 'F8936PII' || formName === 'F8936PV') && this.currentYear >= '2024';
  }

  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;
  batchSelect = false;
  defaultsMultipleSelected: { subForm: string; order: number }[] = [];
  formDataObj: {
    entityName?: string;
    taxYearBegin?: string;
    taxYearEnd?: string;
    meCode?: string;
    meName?: string;
    taxCode?: string;
    subForm?: string;
    formName?: string;
    schdName?: string;
    partName?: string;
    sectionName?: string;
    taxYearBeginDate?: Date;
    taxYearEndDate?: Date;
  } = {};
  activeTab: string = '';
  selectedTabName = '';
  currentFormHeader: any = null;
  tableHeader: any[] = [];
  formList: any[] = [];
  outerList: any[] = [];
  preFormList: any[] = [];
  postFormList: any[] = [];
  partIVFormList: any[] = [];
  partIVOuterList: any[] = [];
  outerListPreFormList: any[] = [];
  currentYear: string = '';
  calcFormulas: any[] = [];
  formLOV: any[] = [];
  f4136AllFuelCodes: any[] = [];
  lineFuelCodesMap: Record<string, any[]> = {};
  isLoading = false;
  columnWidthsPx: number[] = [];
  private originalTabsList: any[][] = [];
  // Init-calc coordination: fetchModalCalcData() and fetchModalData() subscribe
  // independently, so we can't run validateSubForm() inline in either callback
  // — the data and the formulas might not both be present yet. These flags are
  // reset at the start of each init()/onTabSelect() cycle and flipped to true
  // when the matching response lands; once both are true we run a single pass
  // of formula evaluation so labels (sums, mins, maxes) are populated before
  // the user touches anything.
  private calcDataReady = false;
  private formDataReady = false;

  f8941Parts: string[] = [];
  presentTabClicked: string = '';
  subFormHeaderList: Map<string, string> = new Map();
  subTableHeaderList: Map<string, any[]> = new Map();
  subTableHeaderSelected: any[] = [];

  wizardActiveTab: 'Form 8936' | 'Schedule A' = 'Form 8936';

  /** F7218 Part III — multi-row line 1 table (columns a-h). */
  f7218PartIIIHeader: any = null;
  f7218PartIIIColumns: any[] = [];
  f7218PartIIIRows: { occurrence: number; cells: any[] }[] = [];
  /** Part III line 25 — F7218_III_Total_ColH ("Total of column (h)"). */
  f7218PartIIILine2Cells: any[] = [];
  f7218PartIIILine2Label = 'Total of column (h)';
  showF7218PartIIITable = false;
  /** Raw Part III line-1 + header rows from API, kept for Reset after processF7218PartIII strips formList. */
  private originalF7218PartIIIRawItems: any[] = [];
  /** Occurrence-1 data cells keyed by column letter (a-h) — template for new add-row cells. */
  private f7218PartIIICellTemplateByColumn: Map<string, any> = new Map();
  /**
   * Column-level TRANS_DETAILS_KEY / ACCT_REF_KEY from load (shared across occurrences per column).
   * Used so updates send existing keys instead of null insert-style payloads.
   */
  private f7218PartIIIColumnPersistKeys = new Map<number, { TRANS_DETAILS_KEY: any; ACCT_REF_KEY: any }>();
  /** Persisted line-1 cells removed from the grid — saved with IS_CHANGED_FLAG = 'D'. */
  private f7218PartIIIDeletedCells: any[] = [];

  @Input('grid-data')
  set _gridData(gridData: string) {
    try {
      const data = typeof gridData === 'string' ? safeJsonInput<any>(gridData, null, 'credits-part-iii.parse1') : gridData;
      this.gridData = data;
      this.tryInit();
    } catch (e) { console.log('Grid data input not yet stable.'); }
  }

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private creditsPartIIIService: CreditsPartIIIService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();

    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));

    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));

    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);

    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);

    this.cdr.detectChanges();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.rowData || Object.keys(this.rowData).length === 0) return;
    if (!this.GENERAL_CONFIG?.base_url) return;
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    this.currentYear = String(this.filterParams?.tax_year || this.rowData?.TAX_YEAR || '');
    const subForm = this.rowData.SUB_FORM || this.rowData.FORM_NAME || '';
    const subParts = String(subForm).split('~');
    const tyBegin = this.rowData.TAX_YEAR_BEGIN || this.rowData.TAX_YEAR || '';
    const tyEnd = this.rowData.TAX_YEAR_END || this.rowData.TAX_YEAR || '';
    this.formDataObj = {
      entityName: this.rowData.LE_NAME || this.rowData.ENTITY_NAME || '',
      taxYearBegin: tyBegin,
      taxYearEnd: tyEnd,
      meCode: this.rowData.ME_CODE || '',
      meName: this.rowData.ME_NAME || '',
      taxCode: this.rowData.TAX_CODE || this.rowData.CDR_NO || '',
      subForm: subForm,
      formName: subParts[0] || '',
      schdName: subParts[1] || '',
      partName: subParts[2] || '',
      sectionName: subParts[3] || '',
      taxYearBeginDate: tyBegin ? this.safeParseDate(tyBegin) : undefined,
      taxYearEndDate: tyEnd ? this.safeParseDate(tyEnd) : undefined
    };
    if (this.gridData && Array.isArray(this.gridData) && this.gridData.length > 1) {
      this.batchSelect = true;
      this.defaultsMultipleSelected = this.gridData.map((item: any, idx: number) => ({
        subForm: item.SUB_FORM || item.FORM_NAME || item.subForm || `Form ${idx + 1}`,
        order: idx
      }));
      if (this.defaultsMultipleSelected.length > 0) {
        this.activeTab = this.defaultsMultipleSelected[0].subForm;
        this.selectedTabName = this.activeTab;
      }
    } else {
      this.batchSelect = false;
      this.defaultsMultipleSelected = this.formDataObj.subForm ? [{ subForm: this.formDataObj.subForm, order: 0 }] : [];
      this.activeTab = this.formDataObj.subForm || '';
      this.selectedTabName = this.activeTab || '';
    }
    this.calcDataReady = false;
    this.formDataReady = false;
    this.fetchModalCalcData();
    this.fetchDropdownData();
    this.fetchModalData();
  }

  onTabSelect(tab: { subForm: string }): void {
    this.activeTab = tab.subForm;
    this.selectedTabName = tab.subForm;
    this.presentTabClicked = '';
    this.f8941Parts = [];
    this.subFormHeaderList = new Map();
    this.subTableHeaderList = new Map();
    this.subTableHeaderSelected = [];
    this.wizardActiveTab = 'Form 8936';
    this.clearF7218PartIIIState();

    const subParts = String(tab.subForm || '').split('~');
    this.formDataObj = {
      ...this.formDataObj,
      subForm: tab.subForm,
      formName: subParts[0] || '',
      schdName: subParts[1] || '',
      partName: subParts[2] || '',
      sectionName: subParts[3] || ''
    };

    this.calcDataReady = false;
    this.formDataReady = false;
    this.fetchModalCalcData();
    this.fetchDropdownData();
    this.fetchModalData();
  }

  /**
   * Runs once after both fetchModalCalcData() and fetchModalData() have
   * returned for the current init/tabSelect cycle. Drives a full pass of
   * formula evaluation so calculated cells (e.g. F8936 PII line 2 = 1a+1b+...,
   * line 5 = min(2,4)) are populated before the user edits anything.
   */
  private tryInitialCalc(): void {
    if (!this.calcDataReady || !this.formDataReady) return;
    if (!this.formList || this.formList.length === 0) return;
    this.supplementCalcFormulas();
    if (!this.calcFormulas || this.calcFormulas.length === 0) return;
    this.validateSubForm();
  }

  /**
   * The calcFormulas endpoint (action_code=1ubsuu) sometimes omits formulas
   * that ARE stored on form-data rows (ATTRIB_CALC_FORMULA). Fill the gap so
   * the calculation engine picks them up. Only adds rows whose formula has
   * token references (contains `(`), is not a json-conditional, and is not
   * already covered by the endpoint response.
   */
  private supplementCalcFormulas(): void {
    const allRows = [
      ...this.formList, ...this.preFormList,
      ...this.postFormList, ...this.partIVFormList
    ];
    for (const row of allRows) {
      const f = row.ATTRIB_CALC_FORMULA;
      if (!f || row.IS_EDITABLE_CELL === 'Y' || f.startsWith('json') || !f.includes('(')) continue;
      if (this.calcFormulas.some((c: any) => c.LINE_ATTRIB_KEY === row.LINE_ATTRIB_KEY)) continue;
      this.calcFormulas.push({
        FORM_LINE_KEY: row.FORM_LINE_KEY,
        LINE_NO: row.LINE_NO,
        ATTRIB_COL: row.ATTRIB_COL,
        LINE_ORDER: row.LINE_ORDER,
        LINE_ATTRIB_KEY: row.LINE_ATTRIB_KEY,
        ATTRIB_CALC_FORMULA: f,
        EDIT_TYPE: row.EDIT_TYPE
      });
    }
  }

  private safeParseDate(value: any): Date | undefined {
    if (!value) return undefined;
    try {
      const d = (value instanceof Date) ? value : new Date(value);
      return isNaN(d.getTime()) ? undefined : d;
    } catch (e) {
      return undefined;
    }
  }

  /**
   * Replicates legacy AngularJS behavior: parse json formula on dateVarious cells
   * to populate item.taxYearBeginDate / item.taxYearEndDate (used as min/max in
   * the datepicker), and convert ATTRIB_VALUE to a Date object when not 'VARIOUS'.
   */
  processDateField(item: any): void {
    if (!item || item.EDIT_TYPE !== 'custom_date_various') return;
    if (item.ATTRIB_VALUE !== 'VARIOUS' && item.ATTRIB_VALUE) {
      const parsed = this.safeParseDate(item.ATTRIB_VALUE);
      if (parsed) item.ATTRIB_VALUE = parsed;
    }
    const formula = item.ATTRIB_CALC_FORMULA;
    if (formula && typeof formula === 'string' && formula.substring(0, 4) === 'json') {
      try {
        const subFormula = safeJsonInput<any>(formula.slice(4), null, 'credits-part-iii.parse2');
        if (subFormula[0]?.DEFAULT_FROM_DATE === 'CURR_RP_BEGIN') {
          item.taxYearBeginDate = this.safeParseDate(item.TAX_YEAR_BEGIN || this.formDataObj.taxYearBegin);
        } else if (subFormula[0]?.DEFAULT_FROM_DATE === 'CURR_TY_BEGIN') {
          item.taxYearBeginDate = this.safeParseDate('01/01/' + this.currentYear);
        } else if (subFormula[0]?.DEFAULT_FROM_DATE) {
          item.taxYearBeginDate = this.safeParseDate(subFormula[0].DEFAULT_FROM_DATE);
        }
        if (subFormula[1]?.DEFAULT_TO_DATE === 'CURR_RP_END') {
          item.taxYearEndDate = this.safeParseDate(item.TAX_YEAR_END || this.formDataObj.taxYearEnd);
        } else if (subFormula[1]?.DEFAULT_TO_DATE === 'CURR_TY_END') {
          item.taxYearEndDate = this.safeParseDate('12/31/' + this.currentYear);
        } else if (subFormula[1]?.DEFAULT_TO_DATE) {
          item.taxYearEndDate = this.safeParseDate(subFormula[1].DEFAULT_TO_DATE);
        }
      } catch (e) { /* bad json formula — ignore */ }
    }
    if (!item.taxYearBeginDate) item.taxYearBeginDate = this.formDataObj.taxYearBeginDate;
    if (!item.taxYearEndDate) item.taxYearEndDate = this.formDataObj.taxYearEndDate;
  }

  /**
   * Propagates formName / schdName / partName / sectionName from formDataObj
   * onto each row in formList so save() can emit them in the payload.
   */
  private assignSubFormParts(rows: any[]): void {
    if (!Array.isArray(rows) || rows.length === 0) return;
    const fd = this.formDataObj;
    for (const row of rows) {
      if (!row) continue;
      if (row.formName === undefined || row.formName === '') {
        row.formName = fd.formName || row.FORM_NAME || '';
      }
      if (row.schdName === undefined || row.schdName === '') {
        row.schdName = fd.schdName || row.SCHEDULE_NAME || '';
      }
      if (row.partName === undefined || row.partName === '') {
        row.partName = fd.partName || row.PART_NO || '';
      }
      if (row.sectionName === undefined || row.sectionName === '') {
        row.sectionName = fd.sectionName || row.SECTION_NAME || '';
      }
    }
  }

  private getFormPieces(): { formNo: string; scheduleName: string; partNo: string; sectionName: string } {
    const selected = this.batchSelect
      ? this.defaultsMultipleSelected.find((x) => x.subForm === this.activeTab)?.subForm || this.activeTab
      : (this.formDataObj.subForm || this.activeTab);
    const split = (selected || '').split('~');
    return {
      formNo: split[0] || '',
      scheduleName: split[1] || '',
      partNo: split[2] || '',
      sectionName: split[3] || ''
    };
  }

  private fetchModalCalcData(): void {
    if (!this.activeTab) return;
    const parts = this.getFormPieces();
    const params: any = {
      action_code: '1ubsuu',
      tax_year: this.filterParams?.tax_year || this.rowData?.TAX_YEAR,
      scenario: this.filterParams?.scenario || this.rowData?.SCENARIO,
      jcd_key: this.filterParams?.jcd_key || this.rowData?.JCD_KEY || this.globalService?.jcd_key,
      form_no: parts.formNo,
      schedule_name: parts.scheduleName,
      part_no: parts.partNo,
      section_name: parts.sectionName
    };
    const body = new URLSearchParams();
    Object.keys(params).forEach((k) => {
      if (params[k] !== undefined && params[k] !== null) {
        body.set(k, String(params[k]));
      }
    });
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    this.http.post<any>(`${this.GENERAL_CONFIG.base_url}/loadJsonObject`, body.toString(), { headers, withCredentials: true })
      .pipe(catchError(() => of({ jsonObject: [] })), takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        this.calcFormulas = Array.isArray(data?.jsonObject) ? data.jsonObject : [];
        this.calcDataReady = true;
        this.tryInitialCalc();
      });
  }

  private fetchDropdownData(): void {
    this.fetchLOVF4136();

    if (this.formNo === 'F4136') {
      this.fetchF4136FuelCodes();
      this.fetchAllF4136LineCodes();
    } else {
      this.f4136AllFuelCodes = [];
      this.lineFuelCodesMap = {};
    }
  }

  private fetchLOVF4136(): void {
    const params: any = {
      action_code: 'sjh60s',
      sso_id: this.USER_SETTINGS?.user?.sso_id || '',
      tax_year: this.filterParams?.tax_year || this.rowData?.TAX_YEAR,
      scenario: this.filterParams?.scenario || this.rowData?.SCENARIO,
      jcd_key: this.filterParams?.jcd_key || this.rowData?.JCD_KEY || this.globalService?.jcd_key,
      form_no: this.formNo
    };
    const body = new URLSearchParams();
    Object.keys(params).forEach((k) => {
      if (params[k] !== undefined && params[k] !== null) {
        body.set(k, String(params[k]));
      }
    });
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    this.http.post<any>(`${this.GENERAL_CONFIG.base_url}/loadJsonObject`, body.toString(), { headers, withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        this.formLOV = Array.isArray(data?.jsonObject) ? data.jsonObject : [];
        if (this.formNo === 'F8941') {
          const lovParts = this.formLOV
            .map((o: any) => o?.CODE_DESC)
            .filter((v: any) => v !== null && v !== undefined && v !== '');
          if (lovParts.length > 0) {
            // LOV is the authoritative source for inner-pill ordering.
            this.f8941Parts = lovParts;
          }
          if (!this.presentTabClicked && this.f8941Parts.length > 0) {
            this.presentTabClicked = this.f8941Parts[0];
            this.subTableHeaderSelected = this.subTableHeaderList.get(this.presentTabClicked) || [];
          }
        }
        this.cdr.detectChanges();
      });
  }

  private buildF8941SubTabs(list: any[]): void {
    this.subFormHeaderList = new Map();
    this.subTableHeaderList = new Map();
    const partNames = Array.from(new Set(list
      .map((x: any) => x?.PART_NO)
      .filter((p: any) => p !== null && p !== undefined && p !== ''))) as string[];
    for (const part of partNames) {
      const fh = list.find((x: any) => x.ROW_TYPE === 'FH' && x.PART_NO === part);
      if (fh) this.subFormHeaderList.set(part, fh.DESCRIPTION);
      this.subTableHeaderList.set(part, list.filter((x: any) => x.ROW_TYPE === 'TH' && x.PART_NO === part));
    }
    // Preserve LOV order if already fetched; never overwrite when LOV has arrived.
    // The fetchLOVF4136() callback will overwrite f8941Parts when it lands.
    if (!this.f8941Parts || this.f8941Parts.length === 0) {
      this.f8941Parts = partNames;
    }
    if (!this.presentTabClicked || !this.subTableHeaderList.has(this.presentTabClicked)) {
      this.presentTabClicked = this.f8941Parts[0] || '';
    }
    this.subTableHeaderSelected = this.subTableHeaderList.get(this.presentTabClicked) || [];
  }

  onF8941InnerTabSelect(part: string): void {
    this.presentTabClicked = part;
    this.subTableHeaderSelected = this.subTableHeaderList.get(part) || [];
    this.cdr.detectChanges();
  }

  getF8941FormHeaderName(): string {
    return this.subFormHeaderList.get(this.presentTabClicked) || (this.currentFormHeader?.DESCRIPTION || '');
  }

  onWizardTabSelect(tab: 'Form 8936' | 'Schedule A'): void {
    this.wizardActiveTab = tab;
    this.selectedTabName = tab;
    this.cdr.detectChanges();
  }

  checkBoxHandler8912(lineKey: any, attribKey: any, attribCol: any): void {
    if (lineKey == null) return;
    const target = this.formList.find(
      (x: any) => x.FORM_LINE_KEY === lineKey && x.ATTRIB_COL === attribCol && x.LINE_ATTRIB_KEY === attribKey
    );
    if (target) {
      target.IS_CHANGED_FLAG = 'Y';
    }
    this.validateSubForm();
  }

  checkBoxHandler8941(lineKey: any, attribKey: any, attribCol: any): void {
    const siblings = this.formList.filter(
      (x: any) => x.FORM_LINE_KEY === lineKey && x.EDIT_TYPE === 'radio' && x.IS_FORM_DISPLAY === 'Y'
    );
    for (const sibling of siblings) {
      sibling.ATTRIB_VALUE = (sibling.LINE_ATTRIB_KEY === attribKey)
        ? (sibling.ATTRIB_VALUE === 'true' ? 'false' : 'true')
        : 'false';
      sibling.IS_CHANGED_FLAG = 'Y';
    }
    this.validateSubForm();
  }

  private fetchF4136FuelCodes(): void {
    const params: any = {
      action_code: '2yt3f7',
      tax_year: this.filterParams?.tax_year || this.rowData?.TAX_YEAR,
      scenario: this.filterParams?.scenario || this.rowData?.SCENARIO,
      jcd_key: this.filterParams?.jcd_key || this.rowData?.JCD_KEY || this.globalService?.jcd_key
    };
    const body = new URLSearchParams();
    Object.keys(params).forEach((k) => {
      if (params[k] !== undefined && params[k] !== null) {
        body.set(k, String(params[k]));
      }
    });
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    this.http.post<any>(`${this.GENERAL_CONFIG.base_url}/loadJsonObject`, body.toString(), { headers, withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        this.f4136AllFuelCodes = Array.isArray(data?.jsonObject) ? data.jsonObject : [];
        this.cdr.detectChanges();
      });
  }

  private fetchAllF4136LineCodes(): void {
    const params: any = {
      action_code: 'obs3gj',
      tax_year: this.filterParams?.tax_year || this.rowData?.TAX_YEAR,
      scenario: this.filterParams?.scenario || this.rowData?.SCENARIO,
      jcd_key: this.filterParams?.jcd_key || this.rowData?.JCD_KEY || this.globalService?.jcd_key
    };
    const body = new URLSearchParams();
    Object.keys(params).forEach((k) => {
      if (params[k] !== undefined && params[k] !== null) {
        body.set(k, String(params[k]));
      }
    });
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    this.http.post<any>(`${this.GENERAL_CONFIG.base_url}/loadJsonObject`, body.toString(), { headers, withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        this.lineFuelCodesMap = {};
        if (Array.isArray(data?.jsonObject)) {
          for (const item of data.jsonObject) {
            const lineNo = item.LINE_NO;
            if (!this.lineFuelCodesMap[lineNo]) {
              this.lineFuelCodesMap[lineNo] = [];
            }
            this.lineFuelCodesMap[lineNo].push({
              FUEL_CODE: item.FUEL_CODE,
              FUEL_DESC: item.FUEL_DESC
            });
          }
        }
        this.cdr.detectChanges();
      });
  }

  onDropdownChange(item: any, value: any): void {
    item.ATTRIB_VALUE = value;
    item.IS_CHANGED_FLAG = 'Y';
    if (this.formNo === 'F7218') {
      const row = this.findF7218PartIIIRowForCell(item);
      if (row) {
        this.recalculateF7218PartIIIRow(row, true);
        this.updateF7218PartIIILine2Totals();
      }
    }
    this.validateSubForm(item);
  }

  private fetchModalData(): void {
    if (!this.activeTab || this.isLoading) return;
    this.isLoading = true;
    this.showFormLoader = true;
    const parts = this.getFormPieces();
    const params: any = {
      action_code: 'hfyq9t',
      sso_id: this.USER_SETTINGS?.user?.sso_id || '',
      tax_year: this.filterParams?.tax_year || this.rowData?.TAX_YEAR,
      scenario: this.filterParams?.scenario || this.rowData?.SCENARIO,
      jcd_key: this.filterParams?.jcd_key || this.rowData?.JCD_KEY || this.globalService?.jcd_key,
      combination_key: this.rowData?.COMBINATION_KEY,
      form_no: parts.formNo,
      schedule_name: parts.scheduleName,
      part_no: parts.partNo,
      section_name: parts.sectionName,
      issue_key: this.rowData?.ISSUE_KEY
    };
    const body = new URLSearchParams();
    Object.keys(params).forEach((k) => {
      if (params[k] !== undefined && params[k] !== null) {
        body.set(k, String(params[k]));
      }
    });
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    this.http.post<any>(`${this.GENERAL_CONFIG.base_url}/loadJsonObject`, body.toString(), { headers, withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data: any) => {
          const list = Array.isArray(data?.jsonObject) ? data.jsonObject : [];
          if (this.formNo === 'F8941') {
            this.currentFormHeader = list.find((x: any) => x.ROW_TYPE === 'FH' && x.PART_NO === 'F8941') || null;
            this.tableHeader = list.filter((x: any) => x.ROW_TYPE === 'TH' && x.PART_NO === 'F8941');
          } else {
            this.currentFormHeader = list.find((x: any) => x.ROW_TYPE === 'FH') || null;
            this.tableHeader = list.filter((x: any) => x.ROW_TYPE === 'TH');
          }
          this.formList = list.slice(1).filter((x: any) => x.ROW_TYPE !== 'TH');

          if (this.formNo === 'F8941') {
            this.buildF8941SubTabs(list);
          } else {
            this.f8941Parts = [];
            this.subFormHeaderList = new Map();
            this.subTableHeaderList = new Map();
            this.subTableHeaderSelected = [];
            this.presentTabClicked = '';
          }

          this.preFormList = [];
          this.postFormList = [];
          this.partIVFormList = [];
          this.partIVOuterList = [];
          this.outerListPreFormList = [];

          if (this.formNo === 'F7207') {
            if (this.currentYear >= '2024') {
              const preFormIdx = this.formList
                .map((e: any) => e.ATTRIB_SHORT_CODE)
                .lastIndexOf('F7207_I_Line6N') + 1;
              this.preFormList = this.formList.slice(0, preFormIdx);

              for (const item of this.preFormList) {
                if (item?.FORM_LINE_KEY != null && item.IS_FORM_DISPLAY === 'Y') {
                  const exists = this.outerListPreFormList.find(
                    (x: any) => x.FORM_LINE_KEY === item.FORM_LINE_KEY
                  );
                  if (!exists) this.outerListPreFormList.push(item);
                }
              }

              const postFormIdx = this.formList
                .map((e: any) => e.DESCRIPTION)
                .indexOf('Advanced Manufacturing Production Credit From Other Entities');
              const partIVIdx = this.formList
                .map((e: any) => e.DESCRIPTION)
                .indexOf('Costs of Producing Applicable Critical Minerals in Current Tax Year');

              if (postFormIdx > -1) {
                // Bound by partIVIdx so postFormList does NOT overlap with
                // partIVFormList. Without this, save() filters both lists for
                // IS_CHANGED_FLAG='Y' and the same row object — shared by
                // reference between the two slices — is sent twice; backend
                // then persists a duplicate Part IV record.
                const postEnd = partIVIdx > postFormIdx ? partIVIdx : this.formList.length;
                this.postFormList = this.formList.slice(postFormIdx, postEnd);
              }
              if (partIVIdx > -1) {
                this.partIVFormList = this.formList.slice(partIVIdx);
                // Backend may still hold duplicate rows from previously-buggy
                // saves; keep first occurrence per LINE_ATTRIB_KEY.
                const seenAttribKeys = new Set<any>();
                this.partIVFormList = this.partIVFormList.filter((r: any) => {
                  const k = r.LINE_ATTRIB_KEY;
                  if (k == null) return true;
                  if (seenAttribKeys.has(k)) return false;
                  seenAttribKeys.add(k);
                  return true;
                });
              }

              if (preFormIdx > 0 && postFormIdx > preFormIdx) {
                this.formList = this.formList.slice(preFormIdx, postFormIdx);
              }
            } else if (this.currentYear && this.currentYear <= '2023') {
              const preFormIdx = this.formList
                .map((e: any) => e.DESCRIPTION)
                .lastIndexOf('Check to indicate whether eligible components include property produced at a facility taken into account for which\na credit under section 48C is being claimed') + 1;
              this.preFormList = this.formList.slice(0, preFormIdx);

              const postFormIdx = this.formList
                .map((e: any) => e.DESCRIPTION)
                .indexOf('Advanced Manufacturing Production Credit From Other Entities');
              if (postFormIdx > -1) {
                this.postFormList = this.formList.slice(postFormIdx);
              }

              if (preFormIdx > 0 && postFormIdx > preFormIdx) {
                this.formList = this.formList.slice(preFormIdx, postFormIdx);
              }
            }
          } else if (this.formNo === 'F4136' && this.currentYear >= '2025') {
            this.preFormList = this.formList.filter((item: any) => item.PART_NO === 'I');

            for (const item of this.preFormList) {
              if (item?.FORM_LINE_KEY != null && item.IS_FORM_DISPLAY === 'Y') {
                const exists = this.outerListPreFormList.find(
                  (x: any) => x.FORM_LINE_KEY === item.FORM_LINE_KEY
                );
                if (!exists) this.outerListPreFormList.push(item);
              }
            }

            this.formList = this.formList.filter((item: any) => item.PART_NO !== 'I');
          }

          if (this.formNo === 'F7218') {
            this.captureF7218PartIIIRawItems(this.formList);
            this.processF7218PartIII(this.formList);
          } else {
            this.originalF7218PartIIIRawItems = [];
            this.clearF7218PartIIIState();
          }

          this.assignSubFormParts(this.formList);
          this.assignSubFormParts(this.preFormList);
          this.assignSubFormParts(this.postFormList);
          this.assignSubFormParts(this.partIVFormList);

          for (const row of this.formList) this.processDateField(row);
          for (const row of this.preFormList) this.processDateField(row);
          for (const row of this.postFormList) this.processDateField(row);
          for (const row of this.partIVFormList) this.processDateField(row);

          this.outerList = [];
          for (const item of this.formList) {
            if (item?.FORM_LINE_KEY != null) {
              if (this.shouldSkipOuterListRow(item)) {
                continue;
              }
              const exists = this.outerList.find((x: any) => x.FORM_LINE_KEY === item.FORM_LINE_KEY && x.IS_FORM_DISPLAY === 'Y');
              if (!exists && item.IS_FORM_DISPLAY === 'Y') {
                this.outerList.push(item);
              }
            }
          }
          this.partIVOuterList = [];
          for (const item of this.partIVFormList) {
            if (item?.FORM_LINE_KEY != null && item.IS_FORM_DISPLAY === 'Y') {
              const exists = this.partIVOuterList.find((x: any) => x.FORM_LINE_KEY === item.FORM_LINE_KEY);
              if (!exists) this.partIVOuterList.push(item);
            }
          }
          this.computeColumnWidths();
          this.captureOriginalSnapshot();
          this.showFormLoader = false;
          this.isLoading = false;
          this.formDataReady = true;
          this.tryInitialCalc();
          this.cdr.detectChanges();
        },
        () => {
          this.currentFormHeader = null;
          this.tableHeader = [];
          this.formList = [];
          this.outerList = [];
          this.preFormList = [];
          this.postFormList = [];
          this.partIVFormList = [];
          this.partIVOuterList = [];
          this.outerListPreFormList = [];
          this.originalTabsList = [];
          this.columnWidthsPx = [];
          this.clearF7218PartIIIState();
          this.showFormLoader = false;
          this.isLoading = false;
          this.formDataReady = true;
          // No tryInitialCalc on error: formList is empty, the guard inside
          // tryInitialCalc would short-circuit anyway.
          this.cdr.detectChanges();
        }
      );
  }

  private clearF7218PartIIIState(): void {
    this.f7218PartIIIHeader = null;
    this.f7218PartIIIColumns = [];
    this.f7218PartIIIRows = [];
    this.f7218PartIIILine2Cells = [];
    this.f7218PartIIILine2Label = 'Total of column (h)';
    this.f7218PartIIICellTemplateByColumn.clear();
    this.f7218PartIIIColumnPersistKeys.clear();
    this.f7218PartIIIDeletedCells = [];
    this.showF7218PartIIITable = false;
  }

  /** Build column-level persist keys from the load snapshot (one key per LINE_ATTRIB_KEY). */
  private buildF7218PartIIIColumnPersistKeyMap(): void {
    this.f7218PartIIIColumnPersistKeys.clear();
    for (const item of this.originalF7218PartIIIRawItems) {
      if (item.PART_NO !== 'III' || !this.isF7218PartIIILine1(item) || item.ROW_TYPE === 'PH') {
        continue;
      }
      const lineKey = item.LINE_ATTRIB_KEY;
      if (lineKey == null || item.TRANS_DETAILS_KEY == null || String(item.TRANS_DETAILS_KEY).trim() === '') {
        continue;
      }
      if (!this.f7218PartIIIColumnPersistKeys.has(lineKey)) {
        this.f7218PartIIIColumnPersistKeys.set(lineKey, {
          TRANS_DETAILS_KEY: item.TRANS_DETAILS_KEY,
          ACCT_REF_KEY: item.ACCT_REF_KEY ?? null
        });
      }
    }
  }

  /** Apply column-level trans/acct keys when the cell has no key yet. */
  private applyF7218ColumnPersistKeysToCell(cell: any): void {
    if (!cell?.LINE_ATTRIB_KEY) {
      return;
    }
    const keys = this.f7218PartIIIColumnPersistKeys.get(cell.LINE_ATTRIB_KEY);
    if (!keys) {
      return;
    }
    if (cell.TRANS_DETAILS_KEY == null || String(cell.TRANS_DETAILS_KEY).trim() === '') {
      cell.TRANS_DETAILS_KEY = keys.TRANS_DETAILS_KEY;
    }
    if (cell.ACCT_REF_KEY == null || String(cell.ACCT_REF_KEY).trim() === '') {
      cell.ACCT_REF_KEY = keys.ACCT_REF_KEY;
    }
  }

  private f7218PartIIICellMatchesRawSnapshot(cell: any, occurrence: number): boolean {
    const rawCell = this.getF7218PartIIIRawLine1CellsForOccurrence(occurrence)
      .find((c) => c.LINE_ATTRIB_KEY === cell.LINE_ATTRIB_KEY);
    if (!rawCell) {
      return false;
    }
    return String(cell.ATTRIB_VALUE ?? '') === String(rawCell.ATTRIB_VALUE ?? '');
  }

  /** True when the row has user edits or is a new occurrence not yet in the load snapshot. */
  private f7218PartIIIRowHasRealChange(row: { occurrence: number; cells: any[] }): boolean {
    const rawCells = this.getF7218PartIIIRawLine1CellsForOccurrence(row.occurrence);
    if (rawCells.length === 0) {
      return row.cells.some((c) => c.IS_CHANGED_FLAG === 'Y');
    }
    for (const cell of row.cells) {
      const letter = this.getF7218ColumnLetterFromCell(cell);
      if (letter === 'E' || letter === 'H') {
        continue;
      }
      if (cell.IS_CHANGED_FLAG === 'Y' && !this.f7218PartIIICellMatchesRawSnapshot(cell, row.occurrence)) {
        return true;
      }
    }
    return false;
  }

  /** Merge occurrence-specific and column-level persist keys for save payload. */
  private enrichF7218PartIIISaveCell(cell: any, occurrence: number): any {
    const rawCell = this.getF7218PartIIIRawLine1CellsForOccurrence(occurrence)
      .find((c) => c.LINE_ATTRIB_KEY === cell.LINE_ATTRIB_KEY);
    const columnKeys = this.f7218PartIIIColumnPersistKeys.get(cell.LINE_ATTRIB_KEY);
    return {
      ...cell,
      OCCURRENCE: occurrence,
      IS_CHANGED_FLAG: 'Y',
      TRANS_DETAILS_KEY: rawCell?.TRANS_DETAILS_KEY
        ?? cell.TRANS_DETAILS_KEY
        ?? columnKeys?.TRANS_DETAILS_KEY
        ?? null,
      ACCT_REF_KEY: rawCell?.ACCT_REF_KEY
        ?? cell.ACCT_REF_KEY
        ?? columnKeys?.ACCT_REF_KEY
        ?? null
    };
  }

  /** Delete needs the full column key even when a calculated occurrence was never saved. */
  private enrichF7218PartIIIDeleteCell(cell: any, occurrence: number): any | null {
    const rawCell = this.getF7218PartIIIRawLine1CellsForOccurrence(occurrence)
      .find((c) => c.LINE_ATTRIB_KEY === cell.LINE_ATTRIB_KEY);
    const columnKeys = this.f7218PartIIIColumnPersistKeys.get(cell.LINE_ATTRIB_KEY);
    const transDetailsKey = rawCell?.TRANS_DETAILS_KEY
      ?? cell.TRANS_DETAILS_KEY
      ?? columnKeys?.TRANS_DETAILS_KEY
      ?? null;

    if (transDetailsKey == null || String(transDetailsKey).trim() === '') {
      return null;
    }

    return {
      ...cell,
      OCCURRENCE: occurrence,
      IS_CHANGED_FLAG: 'D',
      TRANS_DETAILS_KEY: transDetailsKey,
      ACCT_REF_KEY: rawCell?.ACCT_REF_KEY
        ?? cell.ACCT_REF_KEY
        ?? columnKeys?.ACCT_REF_KEY
        ?? null
    };
  }

  /** F7218 Part III is rendered by the custom add-row grid, not the generic outerList table. */
  private shouldSkipOuterListRow(item: any): boolean {
    return this.formNo === 'F7218'
      && String(item.PART_NO || '').trim().toUpperCase() === 'III';
  }

  /** Preserve Part III column defs + row data before processF7218PartIII removes them from formList. */
  private captureF7218PartIIIRawItems(list: any[]): void {
    this.originalF7218PartIIIRawItems = list
      .filter((x: any) => x.PART_NO === 'III' && (
        x.ROW_TYPE === 'PH'
        || String(x.LINE_NO) === '1'
        || this.isF7218PartIIITotalRow(x)
      ))
      .map((o: any) => ({ ...o }));
  }

  /** Re-attach saved Part III rows so processF7218PartIII can rebuild the add-row table on Reset. */
  private restoreF7218PartIIIRawItems(): void {
    this.formList = this.formList.filter((x: any) =>
      !(x.PART_NO === 'III' && (
        x.ROW_TYPE === 'PH'
        || String(x.LINE_NO) === '1'
        || this.isF7218PartIIITotalRow(x)
      ))
    );
    if (this.originalF7218PartIIIRawItems.length > 0) {
      this.formList.push(...this.originalF7218PartIIIRawItems.map((o: any) => ({ ...o })));
    }
  }

  private compareF7218ColumnOrder(a: any, b: any): number {
    const colA = this.getF7218ColumnLetterFromCell(a);
    const colB = this.getF7218ColumnLetterFromCell(b);
    return colA.localeCompare(colB);
  }

  private getF7218PartIIIColumnKeySet(): Set<number> {
    return new Set(
      this.f7218PartIIIColumns
        .map((c) => c.LINE_ATTRIB_KEY)
        .filter((k) => k != null)
    );
  }

  private getF7218ColumnDefForCell(cell: any): any | undefined {
    if (!cell?.LINE_ATTRIB_KEY) {
      return undefined;
    }
    return this.f7218PartIIIColumns.find((c) => c.LINE_ATTRIB_KEY === cell.LINE_ATTRIB_KEY);
  }

  private extractF7218ColumnLetter(shortCode: string): string {
    const match = String(shortCode || '').match(/F7218_III_([A-H])$/i);
    return match ? match[1].toUpperCase() : String(shortCode || '');
  }

  private getF7218ColumnLetterFromCell(cell: any): string {
    const fromShortCode = this.extractF7218ColumnLetter(cell?.ATTRIB_SHORT_CODE);
    if (/^[A-H]$/.test(fromShortCode)) {
      return fromShortCode;
    }
    const col = String(cell?.ATTRIB_COL || '').toUpperCase();
    if (/^[A-H]$/.test(col)) {
      return col;
    }
    const colDef = this.getF7218ColumnDefForCell(cell);
    if (colDef) {
      return this.getF7218ColumnLetterFromCell(colDef);
    }
    return fromShortCode;
  }

  private sortF7218Cells(cells: any[]): any[] {
    const order = new Map(
      this.f7218PartIIIColumns.map((col, idx) => [col.LINE_ATTRIB_KEY, idx])
    );
    return [...cells].sort((a, b) => {
      const idxA = order.get(a.LINE_ATTRIB_KEY) ?? 999;
      const idxB = order.get(b.LINE_ATTRIB_KEY) ?? 999;
      return idxA - idxB;
    });
  }

  getF7218CellForColumn(row: { cells: any[] }, colDef: any): any | undefined {
    if (!colDef) {
      return undefined;
    }
    return row.cells.find((c) => c.LINE_ATTRIB_KEY === colDef.LINE_ATTRIB_KEY)
      ?? row.cells.find((c) => this.getF7218ColumnLetterFromCell(c) === this.getF7218ColumnLetterFromCell(colDef));
  }

  private isF7218PartIIILine1(item: any): boolean {
    return String(item?.LINE_NO) === '1';
  }

  /** Part III line 25 total cell (F7218_III_Total_ColH) — sum of line-1 column (h). */
  private isF7218PartIIITotalRow(item: any): boolean {
    if (!item || String(item.PART_NO || '').trim().toUpperCase() !== 'III') {
      return false;
    }
    return String(item.ATTRIB_SHORT_CODE || '') === 'F7218_III_Total_ColH'
      || String(item.LINE_NO) === '25';
  }

  private getF7218PartIIITotalCell(): any | undefined {
    const fromTotalCells = this.f7218PartIIILine2Cells.find((c) => this.isF7218PartIIITotalRow(c));
    if (fromTotalCells) {
      return fromTotalCells;
    }
    if (this.f7218PartIIILine2Cells.length === 1) {
      return this.f7218PartIIILine2Cells[0];
    }
    return this.formList.find((c) => this.isF7218PartIIITotalRow(c));
  }

  /** Capture IS_FORM_DISPLAY='Y' line-1 cells as editable templates for new/empty rows. */
  private buildF7218PartIIIEditableColumnTemplates(partIIIItems: any[]): void {
    const columnKeys = this.getF7218PartIIIColumnKeySet();
    for (const item of partIIIItems) {
      if (!this.isF7218PartIIILine1(item) || item.ROW_TYPE === 'PH' || !columnKeys.has(item.LINE_ATTRIB_KEY)) {
        continue;
      }
      if (String(item.IS_FORM_DISPLAY).toUpperCase() !== 'Y') {
        continue;
      }
      const letter = this.getF7218ColumnLetterFromCell(item);
      if (!letter) {
        continue;
      }
      const existing = this.f7218PartIIICellTemplateByColumn.get(letter);
      if (!existing || this.scoreF7218DataCell(item) > this.scoreF7218DataCell(existing)) {
        this.f7218PartIIICellTemplateByColumn.set(letter, {
          ...item,
          OCCURRENCE: 1,
          ATTRIB_VALUE: '',
          TRANS_DETAILS_KEY: null,
          IS_CHANGED_FLAG: 'N'
        });
      }
    }
  }

  private resolveF7218PartIIIEditType(colLetter: string, template: any, colDef: any): string {
    const fromTemplate = template?.EDIT_TYPE;
    if (fromTemplate && fromTemplate !== 'label') {
      return fromTemplate;
    }
    const desc = String(colDef?.ATTRIB_DESCRIPTION || template?.ATTRIB_DESCRIPTION || '').toLowerCase();
    if (colLetter === 'C' && (desc.includes('calendar') || desc.includes('year sold'))) {
      return 'custom_date_various';
    }
    if (colLetter === 'A' && desc.includes('type of fuel')) {
      return 'dropdown';
    }
    if (template?.DATA_TYPE === 'decimal' || template?.EDIT_TYPE === 'currency'
      || colDef?.DATA_TYPE === 'decimal' || colDef?.EDIT_TYPE === 'currency') {
      return 'currency';
    }
    return 'text';
  }

  /** Build one editable line-1 grid cell from column metadata and optional saved cell. */
  private buildF7218PartIIIEditableCell(
    colDef: any,
    occurrence: number,
    existing?: any,
    markChanged = false
  ): any {
    const colLetter = this.getF7218ColumnLetterFromCell(colDef);
    const isCalculated = colLetter === 'E' || colLetter === 'H';
    const template = existing
      || this.f7218PartIIICellTemplateByColumn.get(colLetter)
      || colDef;
    const editType = isCalculated ? 'label' : this.resolveF7218PartIIIEditType(colLetter, template, colDef);
    let dataType = existing?.DATA_TYPE || template.DATA_TYPE || colDef.DATA_TYPE || 'string';
    if (!isCalculated && editType === 'custom_date_various') {
      dataType = 'dateVarious';
    } else if (!isCalculated && (editType === 'currency' || dataType === 'decimal' || dataType === 'number')) {
      dataType = dataType === 'string' ? 'decimal' : dataType;
    }

    const cell = {
      ...template,
      ...colDef,
      LINE_ATTRIB_KEY: colDef.LINE_ATTRIB_KEY,
      FORM_LINE_KEY: colDef.FORM_LINE_KEY ?? template.FORM_LINE_KEY,
      ATTRIB_SHORT_CODE: colDef.ATTRIB_SHORT_CODE ?? template.ATTRIB_SHORT_CODE,
      ATTRIB_COL: colLetter.toLowerCase(),
      IS_FORM_DISPLAY: 'Y',
      OCCURRENCE: occurrence,
      ATTRIB_VALUE: existing?.ATTRIB_VALUE ?? '',
      IS_CHANGED_FLAG: existing?.IS_CHANGED_FLAG ?? (markChanged ? 'Y' : 'N'),
      TRANS_DETAILS_KEY: existing?.TRANS_DETAILS_KEY ?? null,
      DATA_TYPE: isCalculated ? 'decimal' : dataType,
      EDIT_TYPE: editType,
      IS_EDITABLE_CELL: isCalculated ? 'N' : 'Y'
    };

    if (existing) {
      this.applyF7218ColumnPersistKeysToCell(cell);
    } else {
      cell.TRANS_DETAILS_KEY = null;
    }
    if (this.isF7218DateVariousCell(cell)) {
      this.processDateField(cell);
    }
    return cell;
  }

  /** Always keep at least one blank line-1 row for data entry. */
  private ensureF7218PartIIIEntryRow(triggerCdr = true): void {
    if (!this.f7218PartIIIColumns.length) {
      return;
    }
    for (const row of this.f7218PartIIIRows) {
      this.ensureF7218PartIIIRowCellsComplete(row);
      this.syncF7218RowOccurrence(row);
    }
    if (this.f7218PartIIIRows.length === 0) {
      this.addF7218PartIIIRow(triggerCdr, false);
    } else {
      this.f7218PartIIIRows = [...this.f7218PartIIIRows];
    }
  }

  /** Saved line-1 cells: IS_FORM_DISPLAY='Y' rows whose LINE_ATTRIB_KEY matches a column def. */
  private extractF7218PartIIIDataCells(partIIIItems: any[]): any[] {
    const columnKeys = this.getF7218PartIIIColumnKeySet();
    return partIIIItems.filter((x: any) => {
      if (!this.isF7218PartIIILine1(x) || x.ROW_TYPE === 'PH' || !columnKeys.has(x.LINE_ATTRIB_KEY)) {
        return false;
      }
      if (String(x.IS_FORM_DISPLAY).toUpperCase() === 'Y') {
        return true;
      }
      // Reload fallback: persisted row with trans key or value but display flag not set.
      return x.TRANS_DETAILS_KEY != null
        || (x.ATTRIB_VALUE != null && String(x.ATTRIB_VALUE) !== '');
    });
  }

  private scoreF7218DataCell(cell: any): number {
    let score = 0;
    if (String(cell.IS_FORM_DISPLAY).toUpperCase() === 'Y') {
      score += 4;
    }
    if (cell.TRANS_DETAILS_KEY != null) {
      score += 2;
    }
    if (cell.ATTRIB_VALUE != null && String(cell.ATTRIB_VALUE) !== '') {
      score += 1;
    }
    return score;
  }

  /** One cell per column per occurrence — prefer saved Y row over column-definition stub. */
  private dedupeF7218CellsForOccurrence(cells: any[]): any[] {
    const columnKeys = this.getF7218PartIIIColumnKeySet();
    const byKey = new Map<number, any>();
    for (const cell of cells) {
      const key = cell.LINE_ATTRIB_KEY;
      if (!columnKeys.has(key)) {
        continue;
      }
      const existing = byKey.get(key);
      if (!existing || this.scoreF7218DataCell(cell) > this.scoreF7218DataCell(existing)) {
        byKey.set(key, cell);
      }
    }
    return this.sortF7218Cells(Array.from(byKey.values()));
  }

  private normalizeF7218PartIIIDataCell(cell: any): void {
    const colDef = this.getF7218ColumnDefForCell(cell);
    if (!colDef) {
      return;
    }
    const letter = this.getF7218ColumnLetterFromCell(colDef);
    const isCalculated = letter === 'E' || letter === 'H';
    cell.IS_FORM_DISPLAY = 'Y';
    if (isCalculated) {
      cell.IS_EDITABLE_CELL = 'N';
      cell.EDIT_TYPE = 'label';
      cell.DATA_TYPE = cell.DATA_TYPE || 'decimal';
      return;
    }
    const template = this.f7218PartIIICellTemplateByColumn.get(letter);
    if (!cell.EDIT_TYPE || cell.EDIT_TYPE === 'label') {
      cell.EDIT_TYPE = this.resolveF7218PartIIIEditType(letter, template || cell, colDef);
    }
    if (cell.IS_EDITABLE_CELL !== 'Y') {
      cell.IS_EDITABLE_CELL = 'Y';
    }
    if (!cell.DATA_TYPE && (template?.DATA_TYPE || colDef.DATA_TYPE)) {
      cell.DATA_TYPE = template?.DATA_TYPE || colDef.DATA_TYPE;
    }
    if (cell.EDIT_TYPE === 'custom_date_various') {
      cell.DATA_TYPE = 'dateVarious';
    }
  }

  private buildF7218PartIIIRowsFromDataCells(dataCells: any[]): { occurrence: number; cells: any[] }[] {
    const occMap = new Map<number, any[]>();
    for (const cell of dataCells) {
      const occ = this.parseF7218CellOccurrence(cell);
      if (!occMap.has(occ)) {
        occMap.set(occ, []);
      }
      occMap.get(occ)!.push({ ...cell });
    }

    const rows = Array.from(occMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([occurrence, cells]) => ({
        occurrence,
        cells: this.dedupeF7218CellsForOccurrence(cells)
      }));

    for (const row of rows) {
      this.ensureF7218PartIIIRowCellsComplete(row);
      this.syncF7218RowOccurrence(row);
      for (const cell of row.cells) {
        this.normalizeF7218PartIIIDataCell(cell);
        cell.IS_CHANGED_FLAG = 'N';
      }
    }
    return rows;
  }

  private parseF7218CellOccurrence(cell: any, fallback = 1): number {
    const raw = cell?.OCCURRENCE ?? cell?.Occurrence ?? cell?.occurrence;
    const n = parseInt(String(raw ?? fallback), 10);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  }

  /** Fill missing a–h cells from column defs without copying another row's TRANS_DETAILS_KEY. */
  private ensureF7218PartIIIRowCellsComplete(row: { occurrence: number; cells: any[] }): void {
    if (!this.f7218PartIIIColumns.length) {
      return;
    }
    const byKey = new Map<number, any>();
    for (const cell of row.cells) {
      byKey.set(cell.LINE_ATTRIB_KEY, { ...cell });
    }
    const completed: any[] = [];
    for (const colDef of this.f7218PartIIIColumns) {
      const key = colDef.LINE_ATTRIB_KEY;
      const existing = byKey.get(key);
      const cell = existing
        ? { ...existing, OCCURRENCE: row.occurrence }
        : this.buildF7218PartIIIEditableCell(colDef, row.occurrence);
      completed.push(cell);
    }
    row.cells = this.sortF7218Cells(completed);
  }

  /** Ensure every cell in a grid row carries the row's OCCURRENCE for save/dedup. */
  private syncF7218RowOccurrence(row: { occurrence: number; cells: any[] }): void {
    for (const cell of row.cells) {
      cell.OCCURRENCE = row.occurrence;
    }
  }

  private syncAllF7218PartIIIRowOccurrences(): void {
    for (const row of this.f7218PartIIIRows) {
      this.syncF7218RowOccurrence(row);
    }
  }

  /**
   * F7218 Part III line 1 is a multi-row grid (columns a–h). Column definitions
   * arrive as IS_FORM_DISPLAY='N' label rows; user data rows use OCCURRENCE.
   */
  private processF7218PartIII(list: any[]): void {
    this.clearF7218PartIIIState();
    const partIIIItems = list.filter((x: any) => x.PART_NO === 'III');
    if (partIIIItems.length === 0) {
      return;
    }

    this.f7218PartIIIHeader = partIIIItems.find((x: any) => x.ROW_TYPE === 'PH') || null;
    this.f7218PartIIIColumns = partIIIItems
      .filter((x: any) =>
        this.isF7218PartIIILine1(x)
        && x.IS_FORM_DISPLAY === 'N'
        && String(x.ATTRIB_SHORT_CODE || '').startsWith('F7218_III_')
      )
      .sort((a, b) => this.compareF7218ColumnOrder(a, b))
      .filter((col, idx, arr) =>
        arr.findIndex((c) => c.LINE_ATTRIB_KEY === col.LINE_ATTRIB_KEY) === idx
      );

    if (this.f7218PartIIIColumns.length === 0) {
      return;
    }

    this.buildF7218PartIIIColumnPersistKeyMap();
    this.f7218PartIIICellTemplateByColumn.clear();
    this.buildF7218PartIIIEditableColumnTemplates(partIIIItems);

    const dataCells = this.extractF7218PartIIIDataCells(partIIIItems);
    this.f7218PartIIIRows = this.buildF7218PartIIIRowsFromDataCells(dataCells);

    const templateRow = this.f7218PartIIIRows.find((r) => r.occurrence === 1) || this.f7218PartIIIRows[0];
    if (templateRow) {
      for (const colDef of this.f7218PartIIIColumns) {
        const letter = this.getF7218ColumnLetterFromCell(colDef);
        const cell = templateRow.cells.find((c) => c.LINE_ATTRIB_KEY === colDef.LINE_ATTRIB_KEY);
        if (letter && cell) {
          const existing = this.f7218PartIIICellTemplateByColumn.get(letter);
          if (!existing || this.scoreF7218DataCell(cell) >= this.scoreF7218DataCell(existing)) {
            this.f7218PartIIICellTemplateByColumn.set(letter, { ...cell, OCCURRENCE: 1, TRANS_DETAILS_KEY: null });
          }
        }
      }
    }

    this.ensureF7218PartIIIEntryRow(false);
    this.recalculateAllF7218PartIIIRows();
    this.processF7218PartIIIDateFields();
    this.showF7218PartIIITable = true;

    const totalRowItems = partIIIItems.filter((x: any) => this.isF7218PartIIITotalRow(x));
    this.f7218PartIIILine2Cells = [...totalRowItems];
    const totalLabelRow = partIIIItems.find((x: any) => this.isF7218PartIIITotalRow(x));
    if (totalLabelRow?.DESCRIPTION) {
      this.f7218PartIIILine2Label = totalLabelRow.DESCRIPTION;
    }
    this.updateF7218PartIIILine2Totals();

    this.formList = this.formList.filter((x: any) =>
      !(x.PART_NO === 'III' && (this.isF7218PartIIILine1(x) || x.ROW_TYPE === 'PH'))
    );
    this.cdr.detectChanges();
  }

  addF7218PartIIIRow(triggerCdr = true, markChanged = true): void {
    if (!this.f7218PartIIIColumns.length) {
      return;
    }

    const maxOcc = this.f7218PartIIIRows.length > 0
      ? Math.max(...this.f7218PartIIIRows.map((r) => r.occurrence))
      : 0;
    const newOcc = maxOcc + 1;

    const cells = this.f7218PartIIIColumns.map((colDef) =>
      this.buildF7218PartIIIEditableCell(colDef, newOcc, undefined, markChanged)
    );

    this.assignSubFormParts(cells);
    const newRow = { occurrence: newOcc, cells };
    this.f7218PartIIIRows = [...this.f7218PartIIIRows, newRow];
    if (this.f7218PartIIICellTemplateByColumn.size === 0) {
      for (const colDef of this.f7218PartIIIColumns) {
        const letter = this.getF7218ColumnLetterFromCell(colDef);
        const cell = cells.find((c) => c.LINE_ATTRIB_KEY === colDef.LINE_ATTRIB_KEY);
        if (letter && cell) {
          this.f7218PartIIICellTemplateByColumn.set(letter, { ...cell, OCCURRENCE: 1 });
        }
      }
    }
    this.recalculateF7218PartIIIRow(newRow, true);
    this.updateF7218PartIIILine2Totals();
    if (triggerCdr) {
      this.cdr.detectChanges();
    }
  }

  deleteF7218PartIIIRow(rowIndex: number): void {
    const row = this.f7218PartIIIRows[rowIndex];
    if (!row) {
      return;
    }

    if (this.f7218PartIIIRowWasPersisted(row)) {
      const deleteCells = this.buildF7218PartIIIDeleteCellsForOccurrence(row.occurrence, row);
      this.f7218PartIIIDeletedCells.push(...deleteCells);
      this.removeF7218PartIIIOccurrenceFromRawSnapshot(row.occurrence);
    }

    this.f7218PartIIIRows.splice(rowIndex, 1);
    this.ensureF7218PartIIIEntryRow(false);
    this.updateF7218PartIIILine2Totals();
    this.cdr.detectChanges();
  }

  /** True when any cell in the row was previously saved (has a trans details key). */
  private f7218PartIIIRowWasPersisted(row: { occurrence: number; cells: any[] }): boolean {
    const rawCells = this.getF7218PartIIIRawLine1CellsForOccurrence(row.occurrence);
    return rawCells.some((cell) =>
      cell.TRANS_DETAILS_KEY != null && String(cell.TRANS_DETAILS_KEY).trim() !== ''
    ) || row.cells.some((cell) =>
      cell.TRANS_DETAILS_KEY != null && String(cell.TRANS_DETAILS_KEY).trim() !== ''
    );
  }

  /** Line-1 cells for one occurrence from the load snapshot (correct per-occurrence trans keys). */
  private getF7218PartIIIRawLine1CellsForOccurrence(occurrence: number): any[] {
    const columnKeys = this.getF7218PartIIIColumnKeySet();
    const cells = this.originalF7218PartIIIRawItems.filter((x) => {
      if (x.PART_NO !== 'III' || String(x.LINE_NO) !== '1' || x.ROW_TYPE === 'PH') {
        return false;
      }
      if (!columnKeys.has(x.LINE_ATTRIB_KEY)) {
        return false;
      }
      return this.parseF7218CellOccurrence(x) === occurrence;
    });
    return this.dedupeF7218CellsForOccurrence(cells.map((c) => ({ ...c })));
  }

  /**
   * Build delete payload for one grid row. Prefer the load-time snapshot so each
   * occurrence keeps its own TRANS_DETAILS_KEY (not keys copied from row 1).
   */
  private buildF7218PartIIIDeleteCellsForOccurrence(
    occurrence: number,
    row?: { occurrence: number; cells: any[] }
  ): any[] {
    if (row) {
      this.ensureF7218PartIIIRowCellsComplete(row);
    }

    const byColumn = new Map<number, any>();
    for (const cell of this.getF7218PartIIIRawLine1CellsForOccurrence(occurrence)) {
      byColumn.set(cell.LINE_ATTRIB_KEY, cell);
    }
    if (row) {
      for (const cell of row.cells) {
        const existing = byColumn.get(cell.LINE_ATTRIB_KEY);
        if (!existing) {
          byColumn.set(cell.LINE_ATTRIB_KEY, { ...cell });
        } else if (String(cell.ATTRIB_VALUE ?? '') !== String(existing.ATTRIB_VALUE ?? '')) {
          byColumn.set(cell.LINE_ATTRIB_KEY, { ...existing, ATTRIB_VALUE: cell.ATTRIB_VALUE });
        }
      }
    }

    const result: any[] = [];
    for (const colDef of this.f7218PartIIIColumns) {
      const cell = byColumn.get(colDef.LINE_ATTRIB_KEY) || {
        ...colDef,
        OCCURRENCE: occurrence,
        ATTRIB_VALUE: ''
      };
      const deleteCell = this.enrichF7218PartIIIDeleteCell(cell, occurrence);
      if (deleteCell) {
        result.push(deleteCell);
      }
    }
    return this.sortF7218Cells(result);
  }

  /** Drop deleted occurrence from reset snapshot so Reset does not restore removed rows. */
  private removeF7218PartIIIOccurrenceFromRawSnapshot(occurrence: number): void {
    if (!this.originalF7218PartIIIRawItems.length) {
      return;
    }
    this.originalF7218PartIIIRawItems = this.originalF7218PartIIIRawItems.filter((item) => {
      if (!this.isF7218PartIIILine1(item) || item.ROW_TYPE === 'PH') {
        return true;
      }
      return this.parseF7218CellOccurrence(item) !== occurrence;
    });
  }

  private isF7218PartIIIEmptyValue(value: any): boolean {
    if (value == null) {
      return true;
    }
    return String(value).trim() === '';
  }

  /** User-entered columns on line 1 (excludes calculated e and h). */
  private isF7218PartIIIEditableSaveCell(cell: any): boolean {
    const letter = this.getF7218ColumnLetterFromCell(cell);
    if (letter === 'E' || letter === 'H' || this.isF7218CalculatedCell(cell)) {
      return false;
    }
    return cell?.IS_EDITABLE_CELL === 'Y';
  }

  /** Editable column labels that are blank on a row slated for save. */
  private getF7218PartIIIMissingEditableFieldLabels(row: { occurrence: number; cells: any[] }): string[] {
    const missing: string[] = [];
    for (const cell of row.cells) {
      if (!this.isF7218PartIIIEditableSaveCell(cell)) {
        continue;
      }
      if (this.isF7218PartIIIEmptyValue(cell.ATTRIB_VALUE)) {
        const colDef = this.getF7218ColumnDefForCell(cell);
        missing.push(
          colDef?.ATTRIB_DESCRIPTION
          || cell.ATTRIB_DESCRIPTION
          || `column (${this.getF7218ColumnLetterFromCell(cell).toLowerCase()})`
        );
      }
    }
    return missing;
  }

  /** True when every user-editable column on the row has no value. */
  private isF7218PartIIIRowFullyEmpty(row: { occurrence: number; cells: any[] }): boolean {
    let editableCount = 0;
    for (const cell of row.cells) {
      if (!this.isF7218PartIIIEditableSaveCell(cell)) {
        continue;
      }
      editableCount++;
      if (!this.isF7218PartIIIEmptyValue(cell.ATTRIB_VALUE)) {
        return false;
      }
    }
    return editableCount > 0;
  }

  /**
   * Block save when a changed Part III line-1 row is missing any editable field value.
   * A single fully empty default row is allowed (no Part III data to save).
   */
  private validateF7218PartIIIEditableRowsBeforeSave(): string | null {
    if (!this.showF7218PartIIITable || !this.f7218PartIIIRows.length) {
      return null;
    }

    if (this.f7218PartIIIRows.length === 1
      && this.isF7218PartIIIRowFullyEmpty(this.f7218PartIIIRows[0])) {
      return null;
    }

    const issues: string[] = [];
    this.f7218PartIIIRows.forEach((row, idx) => {
      if (!this.f7218PartIIIRowHasRealChange(row)) {
        return;
      }
      const missing = this.getF7218PartIIIMissingEditableFieldLabels(row);
      if (missing.length > 0) {
        issues.push(`row ${idx + 1}: ${missing.join(', ')}`);
      }
    });

    if (issues.length === 0) {
      return null;
    }

    return `Part III line 1 has empty required fields — ${issues.join('; ')}. Please enter values before saving.`;
  }

  isF7218CalculatedCell(cell: any): boolean {
    const letter = this.getF7218ColumnLetterFromCell(cell);
    return letter === 'E' || letter === 'H';
  }

  /** True when the template renders an input/select widget for this cell (not read-only span). */
  isF7218EditableInput(cell: any): boolean {
    if (!cell || cell.IS_EDITABLE_CELL !== 'Y' || this.isF7218CalculatedCell(cell)) {
      return false;
    }
    if (this.isF7218DateVariousCell(cell)) {
      return true;
    }
    const editType = String(cell.EDIT_TYPE || '');
    return editType === 'text' || editType === 'currency' || editType === 'dropdown'
      || cell.DATA_TYPE === 'decimal' || cell.DATA_TYPE === 'number';
  }

  isF7218DateVariousCell(cell: any): boolean {
    if (!cell || this.isF7218CalculatedCell(cell)) {
      return false;
    }
    return cell.EDIT_TYPE === 'custom_date_various' || cell.DATA_TYPE === 'dateVarious';
  }

  private processF7218PartIIIDateFields(): void {
    for (const row of this.f7218PartIIIRows) {
      for (const cell of row.cells) {
        if (this.isF7218DateVariousCell(cell)) {
          this.processDateField(cell);
        }
      }
    }
  }

  private getF7218RowCell(row: { cells: any[] }, letter: string): any | undefined {
    const colDef = this.f7218PartIIIColumns.find((c) => this.getF7218ColumnLetterFromCell(c) === letter);
    if (colDef) {
      return row.cells.find((c) => c.LINE_ATTRIB_KEY === colDef.LINE_ATTRIB_KEY);
    }
    return row.cells.find((c) => this.getF7218ColumnLetterFromCell(c) === letter);
  }

  private parseF7218Numeric(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const n = parseFloat(String(value).replace(/,/g, ''));
    return Number.isFinite(n) ? n : null;
  }

  private formatF7218CalcValue(value: number): string {
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
  }

  /**
   * Per-row formulas for F7218 Part III line 1. Global calcFormulas only resolve
   * the first OCCURRENCE match, so e and h must be computed per grid row.
   *   (e) = (50 - d) / 50
   *   (h) = (e) x (f) x (g)
   * When invalidateIncomplete is false (initial load), keep saved e/h if inputs are incomplete.
   */
  private recalculateF7218PartIIIRow(
    row: { occurrence: number; cells: any[] },
    invalidateIncomplete = false
  ): void {
    this.syncF7218RowOccurrence(row);
    const dCell = this.getF7218RowCell(row, 'D');
    const eCell = this.getF7218RowCell(row, 'E');
    const fCell = this.getF7218RowCell(row, 'F');
    const gCell = this.getF7218RowCell(row, 'G');
    const hCell = this.getF7218RowCell(row, 'H');

    if (eCell) {
      const d = dCell ? this.parseF7218Numeric(dCell.ATTRIB_VALUE) : null;
      if (d !== null) {
        const e = (50 - d) / 50;
        const eVal = Number.isFinite(e) ? this.formatF7218CalcValue(e) : '';
        if (String(eCell.ATTRIB_VALUE ?? '') !== eVal) {
          eCell.ATTRIB_VALUE = eVal;
          eCell.IS_CHANGED_FLAG = 'Y';
        }
      } else if (invalidateIncomplete && String(eCell.ATTRIB_VALUE ?? '') !== '') {
        eCell.ATTRIB_VALUE = '';
        eCell.IS_CHANGED_FLAG = 'Y';
      }
    }

    if (hCell) {
      const e = eCell ? this.parseF7218Numeric(eCell.ATTRIB_VALUE) : null;
      const f = fCell ? this.parseF7218Numeric(fCell.ATTRIB_VALUE) : null;
      const g = gCell ? this.parseF7218Numeric(gCell.ATTRIB_VALUE) : null;
      if (e !== null && f !== null && g !== null) {
        const product = e * f * g;
        const hVal = Number.isFinite(product) ? this.formatF7218CalcValue(product) : '';
        if (String(hCell.ATTRIB_VALUE ?? '') !== hVal) {
          hCell.ATTRIB_VALUE = hVal;
          hCell.IS_CHANGED_FLAG = 'Y';
        }
      } else if (invalidateIncomplete && String(hCell.ATTRIB_VALUE ?? '') !== '') {
        hCell.ATTRIB_VALUE = '';
        hCell.IS_CHANGED_FLAG = 'Y';
      }
    }
  }

  private recalculateAllF7218PartIIIRows(invalidateIncomplete = false): void {
    for (const row of this.f7218PartIIIRows) {
      this.recalculateF7218PartIIIRow(row, invalidateIncomplete);
    }
    this.updateF7218PartIIILine2Totals();
  }

  getF7218PartIIIColumnHTotal(): string {
    let total = 0;
    let hasAny = false;
    for (const row of this.f7218PartIIIRows) {
      const hCell = this.getF7218RowCell(row, 'H');
      const n = hCell ? this.parseF7218Numeric(hCell.ATTRIB_VALUE) : null;
      if (n !== null) {
        total += n;
        hasAny = true;
      }
    }
    if (!hasAny) {
      return '';
    }
    return this.formatF7218CalcValue(total);
  }

  /** Sync Part III line 25 (F7218_III_Total_ColH) with sum of line-1 column (h). */
  private updateF7218PartIIILine2Totals(): void {
    const totalCell = this.getF7218PartIIITotalCell();
    if (!totalCell) {
      return;
    }
    const totalStr = this.getF7218PartIIIColumnHTotal();
    if (String(totalCell.ATTRIB_VALUE ?? '') !== totalStr) {
      totalCell.ATTRIB_VALUE = totalStr;
      totalCell.IS_CHANGED_FLAG = 'Y';
    }
    if (this.calcFormulas?.length) {
      this.runCalcFormulasToFixedPoint();
    }
  }

  /** Mark line 25 total for save when any Part III line-1 row has changes. */
  private ensureF7218PartIIITotalForSave(): void {
    this.updateF7218PartIIILine2Totals();
    const totalCell = this.getF7218PartIIITotalCell();
    if (!totalCell) {
      return;
    }
    const hasLine1Changes = this.f7218PartIIIRows.some((row) => this.f7218PartIIIRowHasRealChange(row));
    if (!hasLine1Changes) {
      return;
    }
    const totalStr = this.getF7218PartIIIColumnHTotal();
    totalCell.ATTRIB_VALUE = totalStr;
    totalCell.IS_CHANGED_FLAG = 'Y';
  }

  private findF7218PartIIIRowForCell(item: any): { occurrence: number; cells: any[] } | undefined {
    if (!item) {
      return undefined;
    }
    const byRef = this.f7218PartIIIRows.find((r) => r.cells.includes(item));
    if (byRef) {
      return byRef;
    }
    if (item.PART_NO !== 'III') {
      return undefined;
    }
    const occ = parseInt(String(item.OCCURRENCE ?? 1), 10);
    return this.f7218PartIIIRows.find((r) => r.occurrence === occ);
  }

  /**
   * Changed Part III line-1 cells with per-row OCCURRENCE.
   * Calculated (e) and (h) are included for every dirty row so each occurrence
   * is saved — not only when the derived value changed after recalc.
   */
  private getF7218PartIIIChangedCells(): any[] {
    if (this.f7218PartIIIColumnPersistKeys.size === 0) {
      this.buildF7218PartIIIColumnPersistKeyMap();
    }
    this.syncAllF7218PartIIIRowOccurrences();
    this.recalculateAllF7218PartIIIRows(false);

    const changed: any[] = [];
    const pushedKeys = new Set<string>();

    for (const row of this.f7218PartIIIRows) {
      const rowDirty = this.f7218PartIIIRowHasRealChange(row);

      for (const cell of row.cells) {
        const letter = this.getF7218ColumnLetterFromCell(cell);
        const isDerived = letter === 'E' || letter === 'H';
        const hasDerivedValue = String(cell.ATTRIB_VALUE ?? '').trim() !== '';
        const changedVsRaw = !isDerived && cell.IS_CHANGED_FLAG === 'Y'
          && !this.f7218PartIIICellMatchesRawSnapshot(cell, row.occurrence);
        const shouldInclude = changedVsRaw
          || (rowDirty && isDerived && hasDerivedValue);

        if (!shouldInclude) {
          continue;
        }

        const key = `${cell.LINE_NO ?? '1'}|${cell.LINE_ATTRIB_KEY}|${row.occurrence}`;
        if (pushedKeys.has(key)) {
          continue;
        }
        pushedKeys.add(key);

        changed.push(this.enrichF7218PartIIISaveCell(cell, row.occurrence));
      }
    }

    this.assignSubFormParts(changed);
    return changed;
  }

  /** Persisted cells removed from the grid — backend deletes via is_changed_flag = 'D'. */
  private getF7218PartIIIDeletedCells(): any[] {
    if (!this.f7218PartIIIDeletedCells.length) {
      return [];
    }
    const seen = new Set<string>();
    const deleted: any[] = [];
    for (const cell of this.f7218PartIIIDeletedCells) {
      const occ = this.parseF7218CellOccurrence(cell);
      const key = `${occ}|${cell.LINE_ATTRIB_KEY}|${cell.TRANS_DETAILS_KEY ?? ''}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      deleted.push({ ...cell, OCCURRENCE: occ, IS_CHANGED_FLAG: 'D' });
    }
    this.assignSubFormParts(deleted);
    return deleted;
  }

  private captureOriginalSnapshot(): void {
    const snapshot = [
      ...this.preFormList.map((o: any) => ({ ...o })),
      ...this.formList.map((o: any) => ({ ...o })),
      ...this.postFormList.map((o: any) => ({ ...o })),
      ...this.partIVFormList.map((o: any) => ({ ...o }))
    ];
    if (snapshot.length > 0) {
      snapshot[0].FORM_NAME = snapshot[0].FORM_NAME || this.activeTab;
    }
    this.originalTabsList = [snapshot];
  }

  getCell(lineKey: any, attribOrder: any): any {
    return this.formList.find((x: any) => x.FORM_LINE_KEY === lineKey && x.ATTRIB_ORDER == attribOrder && x.IS_FORM_DISPLAY === 'Y');
  }

  getCellFrom(list: any[], lineKey: any, attribOrder: any): any {
    return list.find((x: any) => x.FORM_LINE_KEY === lineKey && x.ATTRIB_ORDER == attribOrder && x.IS_FORM_DISPLAY === 'Y');
  }

  getAttributeItemsFor(list: any[], lineKey: any): any[] {
    return list.filter(
      (x: any) => x.FORM_LINE_KEY === lineKey && x.IS_FORM_DISPLAY === 'Y' && x.EDIT_TYPE !== 'radio'
    );
  }

  // Per-column width for the main outerList table. Columns whose data cells
  // contain a dropdown need extra space because the <select> in legacy
  // AngularJS used a fixed 200px width that does not fit into the default
  // 180px fixed-layout column (was clipping over the next column - e.g.
  // F4136 "(a) Type of use").
  private computeColumnWidths(): void {
    const defaultWidth = 180;
    const dropdownWidth = 280;
    if (!this.tableHeader?.length) {
      this.columnWidthsPx = [];
      return;
    }
    const widths: number[] = new Array(this.tableHeader.length).fill(defaultWidth);
    for (const row of this.outerList) {
      const items = this.getAttributeItemsFor(this.formList, row.FORM_LINE_KEY);
      for (let i = 0; i < items.length && i < widths.length; i++) {
        if (items[i]?.EDIT_TYPE === 'dropdown') {
          widths[i] = dropdownWidth;
        }
      }
    }
    this.columnWidthsPx = widths;
  }

  getRadioItemsFrom(list: any[], lineKey: any): any[] {
    return list.filter(
      (x: any) => x.FORM_LINE_KEY === lineKey && x.EDIT_TYPE === 'radio' && x.IS_FORM_DISPLAY === 'Y'
    );
  }

  onRadioChangeIn(list: any[], item: any): void {
    const siblings = list.filter(
      (x: any) => x.FORM_LINE_KEY === item.FORM_LINE_KEY
        && x.ATTRIB_COL === item.ATTRIB_COL
        && x.IS_FORM_DISPLAY === 'Y'
    );
    for (const sibling of siblings) {
      sibling.ATTRIB_VALUE = (sibling.LINE_ATTRIB_KEY === item.LINE_ATTRIB_KEY) ? 'true' : 'false';
      sibling.IS_CHANGED_FLAG = 'Y';
    }
    this.validateSubForm();
  }

  /**
   * Radio behavior with toggle-off support: clicking an unchecked option marks
   * it as selected and clears any sibling in the same column; clicking a
   * checked option clears it (leaving all siblings unchecked). Used for Yes/No
   * radio groups like F8820 line 2 (electing reduced credit under 280C).
   */
  onRadioToggleIn(list: any[], item: any, checked: boolean): void {
    const siblings = list.filter(
      (x: any) => x.FORM_LINE_KEY === item.FORM_LINE_KEY
        && x.ATTRIB_COL === item.ATTRIB_COL
        && x.IS_FORM_DISPLAY === 'Y'
    );
    for (const sibling of siblings) {
      if (sibling.LINE_ATTRIB_KEY === item.LINE_ATTRIB_KEY) {
        sibling.ATTRIB_VALUE = checked ? 'true' : 'false';
      } else if (checked) {
        sibling.ATTRIB_VALUE = 'false';
      }
      sibling.IS_CHANGED_FLAG = 'Y';
    }
    this.validateSubForm();
  }

  onFieldChange(item: any, value: any): void {
    item.ATTRIB_VALUE = value;
    item.IS_CHANGED_FLAG = 'Y';
    if (this.formNo === 'F7218') {
      const row = this.findF7218PartIIIRowForCell(item);
      if (row) {
        this.recalculateF7218PartIIIRow(row, true);
        this.updateF7218PartIIILine2Totals();
      }
    }
    this.validateSubForm(item);
  }

  // Backend returns Latitude/Longitude rows on F7210 (and similar geo-tagged
  // forms) with DATA_TYPE='string' + EDIT_TYPE='text' — same shape as a free
  // text field — so DATA_TYPE alone cannot tell us they're numeric. Match by
  // DESCRIPTION instead so the onlyDigitsDecimal directive applies and rejects
  // letters / special chars at the input level.
  isLatLongRow(item: any): boolean {
    const desc = (item?.DESCRIPTION || '').trim().toLowerCase();
    return desc === 'latitude:' || desc === 'longitude:' || desc === 'latitude' || desc === 'longitude';
  }

  validateSubForm(line?: any): void {
    if (line != null && (line.DATA_TYPE === 'number' || line.DATA_TYPE === 'decimal')) {
      line.IS_CHANGED_FLAG = 'Y';
    }
    const shouldRecompute =
      line == null ||
      line.DATA_TYPE === 'number' ||
      line.DATA_TYPE === 'decimal' ||
      this.formNo === 'F8941';

    if (line && line.DATA_TYPE !== 'number' && line.DATA_TYPE !== 'decimal') {
      line.IS_CHANGED_FLAG = 'Y';
    }
    if (line && this.formNo === 'F7207' && (line.DATA_TYPE === 'number' || line.DATA_TYPE === 'decimal')) {
      line.IS_CHANGED_FLAG = 'Y';
    }

    if (shouldRecompute && this.calcFormulas?.length) {
      this.runCalcFormulasToFixedPoint();
    }
    this.cdr.detectChanges();
  }

  /**
   * Run all `calcFormulas` repeatedly until no formula changes any
   * ATTRIB_VALUE — handles dependency chains like F8835 Part II
   * (1a→2→4→5b→5d→6→8). Capped at 10 passes to guard against runaway
   * cycles in malformed formulas.
   */
  private runCalcFormulasToFixedPoint(): void {
    const MAX_PASSES = 10;
    const lists = [this.formList, this.preFormList, this.postFormList, this.partIVFormList];

    const snapshot = (): string => {
      const parts: string[] = [];
      for (const list of lists) {
        for (const row of list) parts.push(String(row?.ATTRIB_VALUE ?? ''));
      }
      return parts.join('|');
    };

    for (let pass = 0; pass < MAX_PASSES; pass++) {
      const before = snapshot();
      for (let g = 0; g < this.calcFormulas.length; g++) {
        const f = this.calcFormulas[g];
        this.calcAmt(
          f.ATTRIB_CALC_FORMULA,
          f.LINE_NO,
          f.LINE_ATTRIB_KEY,
          f.ATTRIB_COL,
          f.EDIT_TYPE
        );
      }
      const after = snapshot();
      if (before === after) break;
    }
  }

  /**
   * Resolve a formula token to a row in formList (or pre/post/partIV for
   * F7207). Performs direct match first, then an alias fallback for the
   * `*_LINE_AMOUNT` rozjazd between formulas (which omit the column letter)
   * and data rows (which include it as `*(c)_LINE_AMOUNT`).
   */
  private resolveTokenRow(token: string): any | undefined {
    let row = this.formList.find(o => o.ATTRIB_SHORT_CODE === token);
    if (row) return row;

    if (this.formNo === 'F7207') {
      if (this.postFormList?.length) {
        row = this.postFormList.find(o => o.ATTRIB_SHORT_CODE === token);
        if (row) return row;
      }
      if (this.preFormList?.length) {
        row = this.preFormList.find(o => o.ATTRIB_SHORT_CODE === token);
        if (row) return row;
      }
      if (this.partIVFormList?.length) {
        row = this.partIVFormList.find(o => o.ATTRIB_SHORT_CODE === token);
        if (row) return row;
      }
    }

    // Alias: `*_LINE_AMOUNT` (without column letter) → `*(c)_LINE_AMOUNT`
    // (or other column letters). The backend formula for F8835 line 2 is
    // `(F8835_II_1a_LINE_AMOUNT) + …` but the corresponding row in formList
    // is `F8835_II_1a(c)_LINE_AMOUNT`.
    const amountMatch = token.match(/^(.+?)_LINE_AMOUNT$/);
    if (amountMatch) {
      const prefix = amountMatch[1];
      if (!/\([a-z]\)$/.test(prefix)) {
        for (const col of ['c', 'd', 'b', 'a']) {
          const aliased = `${prefix}(${col})_LINE_AMOUNT`;
          row = this.formList.find(o => o.ATTRIB_SHORT_CODE === aliased);
          if (row) return row;
          if (this.formNo === 'F7207') {
            if (this.postFormList?.length) {
              row = this.postFormList.find(o => o.ATTRIB_SHORT_CODE === aliased);
              if (row) return row;
            }
            if (this.preFormList?.length) {
              row = this.preFormList.find(o => o.ATTRIB_SHORT_CODE === aliased);
              if (row) return row;
            }
            if (this.partIVFormList?.length) {
              row = this.partIVFormList.find(o => o.ATTRIB_SHORT_CODE === aliased);
              if (row) return row;
            }
          }
        }
      }
    }

    return undefined;
  }

  /**
   * Resolve a formula token in the original (pre-edit) snapshot — used to
   * detect whether a source value has been edited so IS_CHANGED_FLAG can be
   * set correctly. Mirrors resolveTokenRow's alias logic.
   */
  private resolveOriginalRow(token: string): any | undefined {
    const original = this.originalTabsList.length > 0 ? this.originalTabsList[0] : [];
    let row = original.find((x: any) => x.ATTRIB_SHORT_CODE === token);
    if (row) return row;

    const amountMatch = token.match(/^(.+?)_LINE_AMOUNT$/);
    if (amountMatch) {
      const prefix = amountMatch[1];
      if (!/\([a-z]\)$/.test(prefix)) {
        for (const col of ['c', 'd', 'b', 'a']) {
          const aliased = `${prefix}(${col})_LINE_AMOUNT`;
          row = original.find((x: any) => x.ATTRIB_SHORT_CODE === aliased);
          if (row) return row;
        }
      }
    }
    return undefined;
  }

  /**
   * Resolve the target row for a formula. Mirrors the F7207 fallback in
   * resolveTokenRow — formulas can target rows in the pre/post/partIV lists
   * for F7207 because formList for that form is sliced into multiple
   * sub-lists at fetch time.
   */
  private resolveTargetRow(line_no: string, attrib_key: any): any | undefined {
    let row = this.formList.find(x => x.LINE_NO === line_no && x.LINE_ATTRIB_KEY === attrib_key);
    if (row) return row;
    if (this.formNo === 'F7207') {
      if (this.postFormList?.length) {
        row = this.postFormList.find(x => x.LINE_NO === line_no && x.LINE_ATTRIB_KEY === attrib_key);
        if (row) return row;
      }
      if (this.preFormList?.length) {
        row = this.preFormList.find(x => x.LINE_NO === line_no && x.LINE_ATTRIB_KEY === attrib_key);
        if (row) return row;
      }
      if (this.partIVFormList?.length) {
        row = this.partIVFormList.find(x => x.LINE_NO === line_no && x.LINE_ATTRIB_KEY === attrib_key);
        if (row) return row;
      }
    }
    return undefined;
  }

  calcAmt(formula: string, line_no: string, attrib_key: any, attrib_col: string, edit_type: string): void {
    if (!formula) return;

    if (formula.substring(0, 4) === 'json') {
      try {
        const subFormula = safeJsonInput<any>(formula.slice(4), null, 'credits-part-iii.parse3');
        for (let d = 0; d < subFormula.length; d++) {
          let isConditionResult = false;
          const sourceNameVal = this.formList.filter((o: any) => o.ATTRIB_SHORT_CODE === subFormula[d].SOURCE_NAME);
          if (!sourceNameVal || sourceNameVal.length === 0) continue;

          const sv = sourceNameVal[0].ATTRIB_VALUE;
          if (subFormula[d].CONDITION) {
            if (subFormula[d].CONDITION === '<=' && sv !== undefined && parseInt(sv, 10) <= parseInt(subFormula[d].SOURCE_VALUE, 10)) {
              isConditionResult = true;
            } else if (subFormula[d].CONDITION === '>' && sv !== undefined && parseInt(sv, 10) > parseInt(subFormula[d].SOURCE_VALUE, 10)) {
              isConditionResult = true;
            } else if (subFormula[d].CONDITION === '!=' && sv !== undefined && String(sv).trim() !== subFormula[d].SOURCE_VALUE) {
              isConditionResult = true;
            }
          } else {
            if (sv !== undefined && sv == subFormula[d].SOURCE_VALUE) {
              isConditionResult = true;
            }
          }

          if (isConditionResult) {
            const calcAttribVal = this.formList.filter((x: any) => x.LINE_NO === line_no && x.LINE_ATTRIB_KEY === attrib_key);
            if (calcAttribVal.length > 0) {
              const oldVal = calcAttribVal[0].ATTRIB_VALUE;
              if (subFormula[d].CALC_FORMULA_AMT) {
                const target = this.formList.filter((x: any) => x.ATTRIB_SHORT_CODE === subFormula[d].CALC_FORMULA_AMT);
                if (target.length > 0) calcAttribVal[0].ATTRIB_VALUE = target[0].ATTRIB_CALC_FORMULA;
              } else {
                const target = this.formList.filter((x: any) => x.ATTRIB_SHORT_CODE === subFormula[d].CALC_AMT);
                if (target.length > 0) calcAttribVal[0].ATTRIB_VALUE = target[0].ATTRIB_VALUE;
              }
              calcAttribVal[0].IS_CHANGED_FLAG = 'Y';
              if (CreditsPartIIIComponent.DEBUG_CALC) {
                console.log('[CALC json]', {
                  form: this.formNo,
                  line: line_no,
                  attribShortCode: calcAttribVal[0].ATTRIB_SHORT_CODE,
                  condition: subFormula[d].CONDITION,
                  sourceName: subFormula[d].SOURCE_NAME,
                  sourceValue: sv,
                  oldValue: oldVal,
                  newValue: calcAttribVal[0].ATTRIB_VALUE
                });
              }
              if (subFormula[d].ALERT === 'Y') {
                const targetName = this.formList.filter((x: any) => x.ATTRIB_SHORT_CODE === subFormula[d].ALERT_VALUE);
                if (targetName.length > 0) {
                  this.gtwSnackbarService.openSnackBar(targetName[0].ATTRIB_DESCRIPTION, 'gtw-snackbar--yellow');
                }
              }
            }
          }
        }
      } catch (e) { /* bad json formula — ignore */ }
      return;
    }

    let formulaStr = formula;
    // Token = identifier in parens. Allow a single-letter `(a)/(b)/(c)/(d)`
    // nesting in the middle of the name (F8835 Part II uses these for the
    // `1a(a)_LINE_INPUT`, `1a(c)_LINE_AMOUNT` short codes). Identifiers must
    // start with a letter or underscore.
    const tokenRegex = /\(([A-Za-z_][A-Za-z0-9_]*(?:\([a-z]\))?[A-Za-z0-9_]*)\)/g;
    const tokens = new Set<string>();
    let m: RegExpExecArray | null;
    while ((m = tokenRegex.exec(formulaStr)) !== null) {
      tokens.add(m[1]);
    }

    for (const token of tokens) {
      const row = this.resolveTokenRow(token);
      if (!row) continue;

      const parsedAmt = parseFloat(row.ATTRIB_VALUE);
      const lineAmt = Number.isNaN(parsedAmt) ? 0 : parsedAmt;

      const oldRow = this.resolveOriginalRow(token);
      if (oldRow && lineAmt !== parseFloat(oldRow.ATTRIB_VALUE)) {
        row.IS_CHANGED_FLAG = 'Y';
      }

      // Escape regex special chars (notably `(` and `)`) before building the
      // replacement pattern, then use the `g` flag so all occurrences of the
      // same token in the formula are substituted.
      const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(`\\(${escaped}\\)`, 'g');
      const replacement = lineAmt >= 0 ? String(lineAmt) : `(${lineAmt})`;
      formulaStr = formulaStr.replace(re, replacement);
    }

    try {
      const evalResult = safeEvalFormula(formulaStr, null, 'credits-part-iii.calcAmt');
      if (evalResult != null && typeof evalResult === 'number' && Number.isFinite(evalResult)) {
        const target = this.resolveTargetRow(line_no, attrib_key);
        if (target) {
          const oldVal = target.ATTRIB_VALUE;
          const isMath = formulaStr.substring(0, 4) === 'Math';
          const isDecimal = (target.DATA_TYPE || '').toLowerCase() === 'decimal';
          const newAttribValue = isMath
            ? evalResult
            : (isDecimal ? evalResult.toFixed(2) : evalResult.toFixed());
          // Match legacy semantics: detect actual change by comparing the OLD
          // stored value to the NEW calculated value BEFORE overwriting. If we
          // skip this, integer recalcs (e.g. F8826 line3 = line1 - line2 = -150)
          // are visible on screen but never get IS_CHANGED_FLAG='Y' and the
          // save() filter drops them from the API payload.
          const isActuallyChanged = parseFloat(String(oldVal)) !== parseFloat(String(newAttribValue));
          target.ATTRIB_VALUE = newAttribValue;
          if (isActuallyChanged) {
            target.IS_CHANGED_FLAG = 'Y';
          }
          if (CreditsPartIIIComponent.DEBUG_CALC) {
            console.log('[CALC arith]', {
              form: this.formNo,
              line: line_no,
              attribShortCode: target.ATTRIB_SHORT_CODE,
              originalFormula: formula,
              substitutedFormula: formulaStr,
              oldValue: oldVal,
              newValue: target.ATTRIB_VALUE,
              isChangedFlag: target.IS_CHANGED_FLAG
            });
          }
        }
      }
    } catch (e) {
      // formula eval failed (e.g. unresolved token left in formula) —
      // ignore this cell this pass
    }
  }

  onCheckboxChange(item: any, event: any): void {
    item.ATTRIB_VALUE = event.target.checked ? 'true' : 'false';
    item.IS_CHANGED_FLAG = 'Y';
    this.validateSubForm();
  }

  onRadioChange(item: any): void {
    const siblings = this.formList.filter(
      (x: any) => x.FORM_LINE_KEY === item.FORM_LINE_KEY
        && x.ATTRIB_COL === item.ATTRIB_COL
        && x.IS_FORM_DISPLAY === 'Y'
    );
    for (const sibling of siblings) {
      sibling.ATTRIB_VALUE = (sibling.LINE_ATTRIB_KEY === item.LINE_ATTRIB_KEY) ? 'true' : 'false';
      sibling.IS_CHANGED_FLAG = 'Y';
    }
    this.validateSubForm();
  }

  getRadioItems(lineKey: any): any[] {
    return this.formList.filter(
      (x: any) => x.FORM_LINE_KEY === lineKey && x.EDIT_TYPE === 'radio' && x.IS_FORM_DISPLAY === 'Y'
    );
  }

  toDateInputFormat(value: string): string {
    if (!value) return '';
    const parts = value.split('/');
    if (parts.length !== 3) return value;
    return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
  }

  onDateChange(item: any, event: any): void {
    const val = event.target.value;
    if (!val) {
      item.ATTRIB_VALUE = '';
    } else {
      const [y, m, d] = val.split('-');
      item.ATTRIB_VALUE = `${m}/${d}/${y}`;
    }
    item.IS_CHANGED_FLAG = 'Y';
  }

  /**
   * Handler invoked by <gtw-credits-date-various> after VARIOUS toggle or
   * date selection. The widget already mutated row.ATTRIB_VALUE and set
   * IS_CHANGED_FLAG='Y'. Run calculations + change detection.
   */
  onDateVariousChange(_item: any): void {
    this.validateSubForm();
    this.cdr.detectChanges();
  }

  save(): void {
    if (this.crudLoading) return;

    if (this.formNo === 'F7218' && this.showF7218PartIIITable) {
      const emptyRowMessage = this.validateF7218PartIIIEditableRowsBeforeSave();
      if (emptyRowMessage) {
        this.gtwSnackbarService.openSnackBar(emptyRowMessage, 'gtw-snackbar--red');
        return;
      }
      this.ensureF7218PartIIITotalForSave();
    }

    let changed = this.formList.filter((x: any) => x.IS_CHANGED_FLAG === 'Y');
    if (this.formNo === 'F7207') {
      changed = changed.concat(
        this.preFormList.filter((x: any) => x.IS_CHANGED_FLAG === 'Y'),
        this.postFormList.filter((x: any) => x.IS_CHANGED_FLAG === 'Y'),
        this.partIVFormList.filter((x: any) => x.IS_CHANGED_FLAG === 'Y')
      );
    } else if (this.formNo === 'F4136') {
      changed = changed.concat(
        this.preFormList.filter((x: any) => x.IS_CHANGED_FLAG === 'Y')
      );
    } else if (this.formNo === 'F4255' && changed.length > 0) {
      changed = this.formList;
    } else if (this.formNo === 'F7218') {
      changed = changed
        .filter((x: any) => !(x.PART_NO === 'III' && this.isF7218PartIIILine1(x)))
        .concat(this.getF7218PartIIIChangedCells())
        .concat(this.getF7218PartIIIDeletedCells());
    }
    // Defense in depth: never send the same (LINE_NO, LINE_ATTRIB_KEY, OCCURRENCE)
    // twice — backend would persist a duplicate row.
    const seenSaveKeys = new Set<string>();
    changed = changed.filter((r: any) => {
      const occ = r.OCCURRENCE != null ? String(r.OCCURRENCE) : '1';
      const lineNo = r.LINE_NO != null ? String(r.LINE_NO) : '';
      const k = `${lineNo}|${r.LINE_ATTRIB_KEY}|${occ}`;
      if (seenSaveKeys.has(k)) return false;
      seenSaveKeys.add(k);
      return true;
    });
    if (changed.length === 0) {
      this.gtwSnackbarService.openSnackBar('No changes to save', 'gtw-snackbar--yellow');
      return;
    }
    this.crudLoading = true;
    this.isSaveClicked = true;
    this.cdr.detectChanges();
    const payload = changed.map((x: any) => {
      let attribValue: any = x.ATTRIB_VALUE;
      if (x.EDIT_TYPE === 'custom_date_various' && attribValue !== 'VARIOUS' && attribValue instanceof Date) {
        const dt = attribValue as Date;
        attribValue = ('0' + (dt.getMonth() + 1)).slice(-2)
          + '/' + ('0' + dt.getDate()).slice(-2)
          + '/' + dt.getFullYear();
      }
      return {
        form_name: x.formName || this.formDataObj.formName || '',
        issue_key: x.ISSUE_KEY,
        leid: x.LEID,
        cdr_number: x.CDR_NO,
        reporting_period: x.REPORTING_PERIOD,
        me_code: x.ME_CODE,
        part_no: x.partName || this.formDataObj.partName || '',
        schd_no: x.schdName || this.formDataObj.schdName || '',
        section_name: x.sectionName || this.formDataObj.sectionName || '',
        combination_key: x.COMBINATION_KEY,
        form_key: x.FORM_KEY,
        form_line_key: x.FORM_LINE_KEY,
        line_attrib_key: x.LINE_ATTRIB_KEY,
        attrib_type: x.ATTRIB_TYPE,
        attrib_prop_key: x.ATTRIB_PROP_KEY,
        acct_ref_key: x.ACCT_REF_KEY,
        attrib_value: attribValue,
        trans_details_key: x.TRANS_DETAILS_KEY,
        trans_type_key: x.TRANS_TYPE_KEY,
        is_changed_flag: x.IS_CHANGED_FLAG,
        occurrence: x.OCCURRENCE ? parseInt(String(x.OCCURRENCE), 10) : 1
      };
    });
    const clobSettings = {
      sso_id: this.USER_SETTINGS?.user?.sso_id || '',
      screen_key: this.globalService?.screen_key || this.globalService?.globalParams?.screen_key,
      form_name: this.formDataObj.formName || this.getFormPieces().formNo,
      combination_key: this.rowData?.COMBINATION_KEY
    };
    this.creditsPartIIIService.saveCreditForms(
      this.GENERAL_CONFIG.base_url,
      payload,
      clobSettings,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        if (res?.errorMessage && res.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(res.errorMessage, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar('Credits has been successfully saved/updated', 'gtw-snackbar--green');
          if (this.formNo === 'F7218') {
            this.f7218PartIIIDeletedCells = [];
          }
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      () => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  reset(): void {
    const snapshot = this.originalTabsList && this.originalTabsList.length > 0
      ? this.originalTabsList[0]
      : null;
    if (!snapshot || snapshot.length === 0) {
      this.fetchModalCalcData();
      this.fetchModalData();
      return;
    }
    this.formList = snapshot.map((o: any) => ({ ...o }));
    this.preFormList = [];
    this.postFormList = [];
    this.partIVFormList = [];
    this.partIVOuterList = [];
    this.outerListPreFormList = [];

    if (this.formNo === 'F7207') {
      if (this.currentYear >= '2024') {
        const preFormIdx = this.formList.map((e: any) => e.ATTRIB_SHORT_CODE).lastIndexOf('F7207_I_Line6N') + 1;
        this.preFormList = this.formList.slice(0, preFormIdx);
        for (const item of this.preFormList) {
          if (item?.FORM_LINE_KEY != null && item.IS_FORM_DISPLAY === 'Y') {
            const exists = this.outerListPreFormList.find((x: any) => x.FORM_LINE_KEY === item.FORM_LINE_KEY);
            if (!exists) this.outerListPreFormList.push(item);
          }
        }
        const postFormIdx = this.formList.map((e: any) => e.DESCRIPTION).indexOf('Advanced Manufacturing Production Credit From Other Entities');
        const partIVIdx = this.formList.map((e: any) => e.DESCRIPTION).indexOf('Costs of Producing Applicable Critical Minerals in Current Tax Year');
        if (postFormIdx > -1) {
          const postEnd = partIVIdx > postFormIdx ? partIVIdx : this.formList.length;
          this.postFormList = this.formList.slice(postFormIdx, postEnd);
        }
        if (partIVIdx > -1) {
          this.partIVFormList = this.formList.slice(partIVIdx);
          const seenAttribKeys = new Set<any>();
          this.partIVFormList = this.partIVFormList.filter((r: any) => {
            const k = r.LINE_ATTRIB_KEY;
            if (k == null) return true;
            if (seenAttribKeys.has(k)) return false;
            seenAttribKeys.add(k);
            return true;
          });
          for (const item of this.partIVFormList) {
            if (item?.FORM_LINE_KEY != null && item.IS_FORM_DISPLAY === 'Y') {
              const exists = this.partIVOuterList.find((x: any) => x.FORM_LINE_KEY === item.FORM_LINE_KEY);
              if (!exists) this.partIVOuterList.push(item);
            }
          }
        }
        if (preFormIdx > 0 && postFormIdx > preFormIdx) {
          this.formList = this.formList.slice(preFormIdx, postFormIdx);
        }
      }
    } else if (this.formNo === 'F4136' && this.currentYear >= '2025') {
      this.preFormList = this.formList.filter((item: any) => item.PART_NO === 'I');
      for (const item of this.preFormList) {
        if (item?.FORM_LINE_KEY != null && item.IS_FORM_DISPLAY === 'Y') {
          const exists = this.outerListPreFormList.find((x: any) => x.FORM_LINE_KEY === item.FORM_LINE_KEY);
          if (!exists) this.outerListPreFormList.push(item);
        }
      }
      this.formList = this.formList.filter((item: any) => item.PART_NO !== 'I');
    }

    if (this.formNo === 'F7218') {
      this.restoreF7218PartIIIRawItems();
      this.processF7218PartIII(this.formList);
      this.assignSubFormParts(this.formList);
    } else {
      this.clearF7218PartIIIState();
    }

    this.outerList = [];
    for (const item of this.formList) {
      if (item?.FORM_LINE_KEY != null) {
        if (this.shouldSkipOuterListRow(item)) {
          continue;
        }
        const exists = this.outerList.find((x: any) => x.FORM_LINE_KEY === item.FORM_LINE_KEY && x.IS_FORM_DISPLAY === 'Y');
        if (!exists && item.IS_FORM_DISPLAY === 'Y') {
          this.outerList.push(item);
        }
      }
    }
    this.tryInitialCalc();
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
